// Pure helpers for the Patient Home preview tab (native companion replica).
// Reuses the clinically-governed classifiers (classifyBp / computeBpSummary from
// bpReadings.js — cross-checked by static s04; NEVER duplicate the BP thresholds
// here) and the weight/window helpers. Holds only genuinely-new companion logic +
// the client-side fidelity filters + URL sanitizers.

import type {
  PatientHomeTask,
  PatientHomeOutreach,
  PatientHomeCarePlan,
  PatientHomeGoal,
} from '@/services/PatientHomeService/types';
import { planTargetNum } from './carePlanTargets';
import { visitJoinState, JOIN_OPENS_BEFORE_MS, type VisitJoinState } from '../../../scripts/visit-join-link.mjs';
import { isMilestoneSystemRow } from '../../../scripts/milestone-messages.mjs';
import {
  isSpruceRow, isSpruceEvent, spruceEventLabel, spruceDrawable,
} from '../../../scripts/spruce-rows.mjs';
import { isLabCardRow } from './labResults';
import {
  applyOptimisticSnooze, applyOptimisticResume, taskSnooze, isSnoozed, readTaskContext, type CompletionActor,
} from './task';

/* ── NUMERIC coercion ─────────────────────────────────────────────
   apiv2 returns NUMERIC as JSON strings ("30"); coerce before any math. */
export function num(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

/* ── URL / image sanitizers (ported from the spark's own guards) ───
   React escapes text but NOT href/src — reject javascript:/data: etc. */
export function sanitizeUrl(url: string | null | undefined): string {
  if (!url) return '';
  try {
    const p = new URL(url, window.location.href);
    if (p.protocol === 'http:' || p.protocol === 'https:') return url;
  } catch {
    // relative URLs throw on the bare parse above only if base is missing — base is set, so a throw = malformed
  }
  return '';
}

export function safeImageUrl(url: string | null | undefined): string {
  if (!url) return '';
  try {
    const u = new URL(url, window.location.href);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
  } catch {
    return '';
  }
  return /['"()\\\s]/.test(url) ? '' : url;
}

/* ── Time / date formatting (companion parity) ────────────────────── */
export function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const then = new Date(dateStr).getTime();
  if (!Number.isFinite(then)) return '';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function relativeDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const d = new Date(dateStr); d.setHours(0, 0, 0, 0);
  if (!Number.isFinite(d.getTime())) return '';
  const diff = Math.round((d.getTime() - now.getTime()) / 86400000);
  if (diff === 0) return 'Due today';
  if (diff === 1) return 'Due tomorrow';
  if (diff === -1) return 'Overdue — 1 day';
  if (diff < -1) return `Overdue — ${Math.abs(diff)} days`;
  if (diff <= 7) return `Due in ${diff} days`;
  return `Due ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
}

export type TrendDir = 'improving' | 'worsening' | 'stable' | string | null | undefined;
export function trendArrow(dir: TrendDir): string {
  if (dir === 'improving') return '↓';
  if (dir === 'worsening') return '↑';
  return '→';
}
export function trendLabel(dir: TrendDir): string {
  if (dir === 'improving') return 'Improving';
  if (dir === 'worsening') return 'Watch';
  if (dir === 'stable') return 'Stable';
  return '';
}

/* ── Weight display unit (US program: patients read lb, storage stays kg) ── */
export const KG_TO_LB = 2.2046226218;

// Absolute-comparison target operators (≤/≥/</>/=). reduce_by_pct is a percent and
// reduce/change_by are kg DELTAS — none of those is an absolute weight to convert.
export const TARGET_OP_COMPARISON = /^[<>=≤≥]=?$/;

export function kgToLb(v: unknown): number | null {
  const n = num(v);
  return n == null ? null : Math.round(n * KG_TO_LB * 10) / 10;
}

// Rewrites "NN kg" inside an authored target string to its lb equivalent.
export function weightTextToLb(text: string): string {
  return text.replace(/(\d+(?:\.\d+)?)\s*kg\b/gi, (_, n) => `${Math.round(parseFloat(n) * KG_TO_LB * 10) / 10} lb`);
}

/* ── Goal target lookup (coerces the string NUMERIC) ──────────────── */
export function getGoalTarget(
  goals: PatientHomeGoal[] | undefined,
  measureType: string,
  fallback: number | null = null,
): number | null {
  const goal = (goals || []).find((g) => g.measure_type === measureType);
  if (!goal) return fallback;
  const n = num(goal.target_value_num);
  if (n != null) return n;
  if (goal.target_value_text) {
    const m = goal.target_value_text.match(/[\d.]+/);
    if (m) return parseFloat(m[0]);
  }
  return fallback;
}

/* ── Weight target for display (lb) ────────────────────────────────
   The operator lives ONLY on plan.targets — the patient-home goals rows carry
   neither target_operator nor target_value_text (apiv2 getActiveGoalsForUser). */
export interface PlanTargetEntry { operator?: string | null; value?: unknown; display?: string | null }

export function getWeightTargetLb(
  goals: PatientHomeGoal[] | undefined,
  planTargets?: Record<string, PlanTargetEntry | undefined> | null,
): number | null {
  // Plan first — the care plan is the only author of a target. planTargetNum already
  // returns null for a %-reduction operator, so a percentage target draws no line at
  // all, which is correct: there is no single lb value to draw.
  const fromPlan = planTargetNum(planTargets, 'weight');
  if (fromPlan != null) return kgToLb(fromPlan);
  // Legacy `care_goal` row, kept only as a fallback for periods seeded before the table
  // was retired (2026-08-26 — WF1 no longer writes it, so this is empty for new patients).
  const pt = planTargets?.weight;
  const g = (goals || []).find((x) => x.measure_type === 'weight');
  const op = (g as { target_operator?: string | null } | undefined)?.target_operator ?? pt?.operator;
  if (op != null && !TARGET_OP_COMPARISON.test(String(op).trim())) return null;
  const txt = g?.target_value_text || pt?.display || '';
  // No operator info + %-shaped text ⇒ any parsed number is a percent, not kg.
  if (op == null && /%/.test(txt)) return null;
  return kgToLb(getGoalTarget(goals, 'weight'));
}

// Converts a weight target's display string for lb rendering: "80 kg" → "176.4 lb",
// and a unitless comparison display ("≤82", kg implied) → "≤180.8 lb".
export function weightDisplayToLb(display: string | null | undefined, operator?: string | null): string {
  if (!display) return display || '';
  let out = weightTextToLb(display);
  if (operator != null && TARGET_OP_COMPARISON.test(String(operator).trim())) {
    out = out.replace(/^([<>=≤≥]=?\s*)(\d+(?:\.\d+)?)$/, (_, op, n) => `${op}${Math.round(parseFloat(n) * KG_TO_LB * 10) / 10} lb`);
  }
  return out;
}

/* ── WF77's "Share the care" invite: its own card, never in the hero ring, Next Steps or the
   learning path; still in the staff section so it can be cancelled. Mirrors the spark's invite.js. */
export const SHARE_INVITE_TEMPLATE = 'onb_patient_share_invite';
const SHARE_INVITE_WINDOW_MS = 30 * 86400000;

export function isShareInvite(task: PatientHomeTask | null | undefined): boolean {
  if (!task) return false;
  return task.template_code === SHARE_INVITE_TEMPLATE
    || readTaskContext(task.context_json)?.template_code === SHARE_INVITE_TEMPLATE;
}

export function withoutShareInvite(tasks: PatientHomeTask[] | undefined): PatientHomeTask[] {
  return (tasks || []).filter((t) => !isShareInvite(t));
}

// The card shows while the invite is open, the period is open and the task is under 30 days old.
export function pickShareInvite(
  tasks: PatientHomeTask[] | undefined,
  carePeriodStatus: string | null | undefined,
  nowMs: number = Date.now(),
): PatientHomeTask | null {
  if (carePeriodStatus !== 'pending_alignment' && carePeriodStatus !== 'active') return null;
  return (tasks || []).find((t) => {
    if (!isShareInvite(t) || (t.status !== 'pending' && t.status !== 'in_progress')) return false;
    const url = readTaskContext(t.context_json)?.url;
    if (!sanitizeUrl(typeof url === 'string' ? url : '')) return false;
    const since = Date.parse(String(t.available_at || t.created || ''));
    return Number.isFinite(since) && since <= nowMs && nowMs - since < SHARE_INVITE_WINDOW_MS;
  }) ?? null;
}

/* ── 2-week task completion % (drives the hero ring) ──────────────── */
// null = nothing countable yet. Not 100 (a full "On Track" ring while the baseline tasks are
// the only thing on the plan) and not 0 (reads as "0 % done") — callers render a neutral state.
export function computeRecentCompletion(tasks: PatientHomeTask[] | undefined): number | null {
  const now = new Date();
  const twoWeeksAgo = new Date(now.getTime() - 14 * 86400000);
  const countable = withoutShareInvite(tasks).filter((t) => {
    const availAt = t.available_at ? new Date(t.available_at) : null;
    if (availAt && availAt > now) return false; // future
    if (availAt && availAt < twoWeeksAgo && t.status !== 'completed') return false; // old incomplete
    if (t.status === 'scheduled' && (!availAt || availAt > now)) return false;
    return true;
  });
  if (countable.length === 0) return null;
  const done = countable.filter((t) => t.status === 'completed').length;
  return Math.round((done / countable.length) * 100);
}

/* ── Fidelity filters — COSMETIC mirror of what the patient sees, NOT
   an authz boundary. A coordinator is already authorized to see all of
   this via /views/patient-file; the real redaction stays server-side on
   patient sessions. These only make the preview look like the patient's. */

// Tasks CARRY visible_to_patient → filter faithfully.
export function filterPatientVisibleTasks(tasks: PatientHomeTask[] | undefined): PatientHomeTask[] {
  return (tasks || []).filter((t) => t.visible_to_patient === true);
}

// The companion's Next Steps visibility rule — open tasks the patient can act on NOW
// (scheduled ones only once available_at passes). Shared by NextSteps AND the staff
// "Patient tasks" section; they differ only on the share invite, which NextSteps drops.
export function patientActionableTasks(
  tasks: PatientHomeTask[] | undefined,
  nowMs: number = Date.now(),
): PatientHomeTask[] {
  return (tasks || []).filter((t) => {
    if (t.status === 'pending' || t.status === 'overdue' || t.status === 'in_progress') return true;
    if (t.status === 'scheduled' && t.available_at && new Date(t.available_at).getTime() <= nowMs) return true;
    return false;
  });
}

// Scheduled tasks the patient does NOT see yet (future available_at) — surfaced to
// the care team as a count only.
export function patientUpcomingTasks(
  tasks: PatientHomeTask[] | undefined,
  nowMs: number = Date.now(),
): PatientHomeTask[] {
  return (tasks || []).filter((t) => t.status === 'scheduled'
    && (!t.available_at || new Date(t.available_at).getTime() > nowMs));
}

// Completed patient tasks, newest first (the companion's completed strip order).
export function patientCompletedTasks(tasks: PatientHomeTask[] | undefined): PatientHomeTask[] {
  return (tasks || []).filter((t) => t.status === 'completed')
    .sort((a, b) => new Date(b.completed_at || 0).getTime() - new Date(a.completed_at || 0).getTime());
}

// Content-library filter. Availability is compared as an INSTANT (available_at is
// UTC) vs now — NOT a date-string compare, which hid a task on its creation day.
export function filterVisibleContentTasks(
  tasks: PatientHomeTask[] | undefined,
  nowMs: number = Date.now(),
): PatientHomeTask[] {
  return (tasks || []).filter((t) => {
    if (t.clinical_domain !== 'education' && t.task_type !== 'education') return false;
    if (t.status === 'completed') return true;
    if (t.available_at && new Date(t.available_at).getTime() > nowMs) return false;
    return true;
  });
}

// Messages surface shows only CONVERSATIONAL outreach — drop email + ehr_message
// (one-shot audit records the patient never sees in chat). Mirrors the spark's
// listOutreach filter; leaves sms + portal.
const NON_CONVERSATIONAL_CHANNELS = new Set(['email', 'ehr_message']);
// WF26's PHI-free "open the app" SMS is a DELIVERY event, not a message — the patient's
// chat thread never shows it, so a preview that does is showing a bubble that does not
// exist on their phone. It is also the newest row whenever it fires, which made it the
// preview's headline. Mirrors the spark's own filter (js/clinical-api.js).
const NON_CONVERSATIONAL_MSG_TYPES = new Set(['chat_notification']);

// The patient-home preview line. A badge row and a lab-results card show their first line only, as the
// companion does (js/sections/messages.js): the rest of their body is a card, not a sentence.
export function chatPreviewText(
  row: { body?: unknown; msg_type?: unknown; source?: unknown; direction?: unknown } | null | undefined,
  max = 90,
): string {
  if (isSpruceEvent(row)) return spruceEventLabel(row, 'patient') ?? '';
  if (!spruceDrawable(row)) return '';
  const body = String(row?.body ?? '');
  const firstLineOnly = !!row && (isMilestoneSystemRow(row) || isLabCardRow(row));
  const text = firstLineOnly ? (body.split('\n')[0] ?? '') : body;
  return text.length > max ? `${text.slice(0, max)}...` : text;
}
/* ── The patient's visit card ──────────────────────────────────────
   WF69 stamps `context_json.appointment` on `onb_patient_virtual_visit` and sets due_at to the
   visit time. The card must show that time — "Due tomorrow" is not enough when the patient has
   to be somewhere at 3pm. */

// Elation's `scheduled_date` offset is NOT verified against a real payload. A timestamp with no
// zone is read as the VIEWER's local time, which on a US patient's phone could be hours off —
// the same class as the DOB-off-by-one that shipped to nine patients. So the clock time is shown
// only when the string proves its own zone; otherwise the patient gets the day, which is right
// under either answer. Delete this guard once a real appointment payload settles it.
const HAS_EXPLICIT_ZONE = /(Z|[+-]\d{2}:?\d{2})$/;

export function appointmentWhen(
  scheduledAt: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!scheduledAt) return null;
  const raw = String(scheduledAt).trim();
  const t = Date.parse(raw);
  if (!Number.isFinite(t)) return null;
  const at = new Date(t);
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const delta = Math.round((new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime() - day.getTime()) / 86400000);
  let dayWord: string;
  if (delta === 0) dayWord = 'Today';
  else if (delta === 1) dayWord = 'Tomorrow';
  else dayWord = at.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  if (!HAS_EXPLICIT_ZONE.test(raw)) return dayWord;
  return `${dayWord}, ${at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
}

export type { VisitJoinState };
export const JOIN_OPENS_BEFORE_MIN = JOIN_OPENS_BEFORE_MS / 60000;

// The companion's Join predicate itself, not a copy, so the preview cannot disagree with the phone.
// The replica is inert, so the link is dropped here.
export function visitJoinCta(task: PatientHomeTask | null | undefined, nowMs: number = Date.now()): VisitJoinState {
  return visitJoinState(task, nowMs).state;
}

/** Is this visit today or tomorrow — the window the companion hero promotes. */
export function isVisitImminent(scheduledAt: string | null | undefined, now: Date = new Date()): boolean {
  if (!scheduledAt) return false;
  const t = Date.parse(String(scheduledAt));
  if (!Number.isFinite(t)) return false;
  const at = new Date(t);
  if (at.getTime() < now.getTime()) return false; // already passed — not a heads-up
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const delta = Math.round((new Date(at.getFullYear(), at.getMonth(), at.getDate()).getTime() - day.getTime()) / 86400000);
  return delta === 0 || delta === 1;
}

// A transcript or an unknown clinic-line type is never conversation here, as in the patient app.
export function filterConversationalOutreach(rows: PatientHomeOutreach[] | undefined): PatientHomeOutreach[] {
  return (rows || []).filter((r) => !NON_CONVERSATIONAL_CHANNELS.has(String(r.channel))
    && !NON_CONVERSATIONAL_MSG_TYPES.has(String(r.msg_type)) && spruceDrawable(r));
}

// apiv2 get_patient_home's default `clinical_outreach_limit`: the tail holds at most this many rows.
export const OUTREACH_TAIL_CAP = 50;

export interface UnreadForPatient {
  count: number;
  // The tail was full and its oldest row was created after the marker, so more may sit beyond it: show `${count}+`.
  more: boolean;
}

function createdMs(row: { created?: unknown; created_at?: unknown }): number {
  return Date.parse(String(row.created ?? row.created_at ?? ''));
}

// The clinic line: only what the care team sent the patient by text, or a call of ours they missed.
function countsAsUnread(row: PatientHomeOutreach): boolean {
  if (!isSpruceRow(row)) return row.source !== 'patient';
  return row.direction === 'outbound' && (row.msg_type === 'spruce_sms' || row.msg_type === 'spruce_missed_call');
}

// The companion's rows, but timed by `created` against the server watermark for every row, on purpose (the companion
// times an ordinary row by `sent_at` against its local marker): a clinic-line row is written up to 26 h after `sent_at`.
export function unreadForPatient(
  tail: readonly PatientHomeOutreach[] | null | undefined,
  marker: string | null | undefined,
): UnreadForPatient {
  const all = tail || [];
  const seenMs = marker == null ? NaN : Date.parse(marker);
  const noMarker = marker == null || !Number.isFinite(seenMs);
  const isNew = (r: PatientHomeOutreach) => noMarker || createdMs(r) > seenMs;
  const count = filterConversationalOutreach([...all]).filter((r) => countsAsUnread(r) && isNew(r)).length;
  const oldest = all.reduce<PatientHomeOutreach | null>((o, r) => (o == null || createdMs(r) < createdMs(o) ? r : o), null);
  const more = all.length >= OUTREACH_TAIL_CAP && oldest != null && isNew(oldest);
  return { count, more };
}

// Outreach does NOT carry visible_to_patient in the projection, so we CANNOT
// filter to patient-safe rows client-side (documented limitation). We only strip
// author_lastname for display parity.
export function stripOutreachAuthorLastName(rows: PatientHomeOutreach[] | undefined): PatientHomeOutreach[] {
  return (rows || []).map((r) => {
    const copy = { ...r };
    delete copy.author_lastname;
    return copy;
  });
}

// Drop care-team-only care-plan metadata for display parity.
export function scrubCarePlanApproval(plan: PatientHomeCarePlan | null): PatientHomeCarePlan | null {
  if (!plan) return null;
  const copy = { ...plan };
  delete copy.approved_by;
  delete copy.approved_at;
  return copy;
}

/* ── Staff-section optimistic overlay ──────────────────────────────────
   Applied to the CURRENT row at merge time, so a concurrent context_json write (WF69's
   appointment stamp) is never masked for the overlay's lifetime. */
export type TaskOverride =
  | { kind: 'status'; patch: Partial<PatientHomeTask> & { status: string } }
  | { kind: 'snooze'; until: string; reason: string; actor?: CompletionActor }
  | { kind: 'resume' };

export function applyTaskOverride(row: PatientHomeTask, o: TaskOverride): PatientHomeTask {
  // Both helpers map over the bundle, so a one-row input yields one row; `?? row` degrades to the
  // unpatched row, which overrideSettled then never settles — so the TTL drops it and the surface
  // falls back to server truth. That is the safe direction for a feature that hides work.
  if (o.kind === 'snooze') return applyOptimisticSnooze([row], row.task_id, o.until, o.reason, o.actor)[0] ?? row;
  if (o.kind === 'resume') return applyOptimisticResume([row], row.task_id)[0] ?? row;
  return { ...row, ...o.patch };
}

export function overrideSettled(row: PatientHomeTask, o: TaskOverride): boolean {
  // PRESENCE, not the exact date: WF17 clamps silently, and waiting for our own `until` would
  // hold the overlay (and the date the operator asked for) over the date that actually landed.
  if (o.kind === 'snooze') return taskSnooze(row) !== null;
  if (o.kind === 'resume') return !isSnoozed(row);
  return row.status === o.patch.status;
}
